import React, { useEffect, useState } from 'react'
import { useIdentity } from '@vp/ubik-context'
import md5 from 'md5'
import { NRLogger } from '../modules/logging/nrLogging'

const AssetStoreContext = React.createContext({})
const AssetStoreProvider = (props) => {
  const { auth } = useIdentity()
  const [assetStore, setAssetStore] = useState()

  useEffect(() => {
    const authHeader = auth?.getAuthorizationHeader()
    if (authHeader) {
      const authorizationProvider = {
        getAuthHeaders: async () => ({
          Authorization: authHeader,
        }),
      }

      NRLogger.info('AssetStoreProvider', {
        phase: 'initialization',
        isAuthHeaderDefined: !!authHeader,
        hasInconsistencies: authHeader !== auth?.getAuthorizationHeader(),
      })

      let VistaAssetStore, SubBrands, Tenants

      const initializeStore = async () => {
        if (typeof window !== 'undefined') {
          try {
            const module = await import('@design-stack-vista/vista-assets-sdk')
            VistaAssetStore = module.VistaAssetStore
            SubBrands = module.SubBrands
            Tenants = module.Tenants

            const newStore = new VistaAssetStore({
              authProvider: authorizationProvider,
              subBrand: SubBrands.VistaPrint,
              experience: 'Email',
              writeTenant: Tenants.VistaCareSupport,
            })
            setAssetStore(newStore)
          } catch (error) {
            NRLogger.error('AssetStoreProvider', {
              phase: 'initialization',
              message: 'Error while initializing asset store',
              error: error?.message,
            })
          }
        }
      }

      initializeStore()
    }
  }, [auth])

  const renameFile = (fileName) => {
    const dot = fileName.lastIndexOf('.')
    const name = fileName.substring(0, dot).toLowerCase()
    const ext = fileName.substring(dot, fileName.length).toLowerCase()
    return md5(name + Date.now()) + ext
  }

  const uploadAsset = async (file) => {
    if (!assetStore) {
      return
    }
    const fileName = renameFile(file.name)
    try {
      const asset = await assetStore.upload({
        file,
        expires: 'P2Y',
      })
      await asset.presign()
      const publicUrl = asset.getUrl({ includeSignature: true })
      await asset.hide()
      return { publicUrl, fileName, name: file.name }
    } catch (e) {
      NRLogger.error('AssetStoreProvider', {
        phase: 'upload',
        message: 'Error while uploading asset',
        error: e?.message,
        fileName,
        plainFileName: file.name,
      })
      return {
        publicUrl: `File attachment upload failed: ${file.name}`,
        fileName,
        name: '',
      }
    }
  }

  const deleteAssetByName = async (name) => {
    const assetToDelete = assetStore.assets.find((asset) => asset.data.info.storage.fileName === name)
    const deleteAsset = assetToDelete?.delete()
    await deleteAsset
  }

  return <AssetStoreContext.Provider value={{ uploadAsset, deleteAssetByName }}>{props.children}</AssetStoreContext.Provider>
}

export { AssetStoreProvider, AssetStoreContext }
